/**
 * Queen B Academy - Enhanced Course Content Index
 * Centralized exports for all enhanced course modules
 * 
 * These enhanced modules provide significantly deeper, more valuable content
 * than the base courseData.js content, including:
 * - Real-world scenarios and case studies
 * - Pro tips from industry veterans
 * - Practical exercises and worksheets
 * - Key takeaways and common mistakes
 * - Extended lesson duration and depth
 */

import CDL_MASTERY_ENHANCED from './cdl_mastery_enhanced';
import DISPATCHER_ENHANCED from './dispatcher_enhanced';
import TRUCKING_BUSINESS_ENHANCED from './trucking_business_enhanced';
import FREIGHT_BROKER_ENHANCED from './freight_broker_enhanced';

// Map course IDs to their enhanced content
export const ENHANCED_COURSE_MAP = {
  'cdl-mastery-101': CDL_MASTERY_ENHANCED,
  'dispatcher-101': DISPATCHER_ENHANCED,
  'trucking-101': TRUCKING_BUSINESS_ENHANCED,
  'freight-broker-101': FREIGHT_BROKER_ENHANCED
};

/**
 * Get enhanced content for a specific lesson
 * @param {string} courseId - The course identifier (e.g., 'cdl-mastery-101')
 * @param {string} moduleId - The module identifier (e.g., 'cdl-m1')
 * @param {string} lessonId - The lesson identifier (e.g., 'cdl-m1-l1')
 * @returns {Object|null} Enhanced lesson content or null if not found
 */
export const getEnhancedLesson = (courseId, moduleId, lessonId) => {
  const course = ENHANCED_COURSE_MAP[courseId];
  if (!course) return null;
  
  const modules = course.enhancedModules;
  if (!modules || !modules[moduleId]) return null;
  
  const lessons = modules[moduleId].lessons;
  if (!lessons || !lessons[lessonId]) return null;
  
  return lessons[lessonId];
};

/**
 * Check if enhanced content exists for a lesson
 * @param {string} courseId - The course identifier
 * @param {string} moduleId - The module identifier
 * @param {string} lessonId - The lesson identifier
 * @returns {boolean} True if enhanced content exists
 */
export const hasEnhancedContent = (courseId, moduleId, lessonId) => {
  return getEnhancedLesson(courseId, moduleId, lessonId) !== null;
};

/**
 * Get all enhanced module data for a course
 * @param {string} courseId - The course identifier
 * @returns {Object|null} Enhanced modules object or null
 */
export const getEnhancedModules = (courseId) => {
  const course = ENHANCED_COURSE_MAP[courseId];
  return course ? course.enhancedModules : null;
};

/**
 * Merge base lesson with enhanced content
 * Enhanced content takes priority where available
 * @param {Object} baseLesson - Original lesson from courseData.js
 * @param {string} courseId - Course identifier
 * @param {string} moduleId - Module identifier
 * @param {string} lessonId - Lesson identifier
 * @returns {Object} Merged lesson content
 */
export const getMergedLessonContent = (baseLesson, courseId, moduleId, lessonId) => {
  const enhanced = getEnhancedLesson(courseId, moduleId, lessonId);
  
  if (!enhanced) {
    return baseLesson;
  }
  
  // Return enhanced content with base lesson as fallback for missing fields
  return {
    ...baseLesson,
    ...enhanced,
    // Explicitly mark as enhanced for UI indicators
    isEnhanced: true,
    // Preserve base ID
    id: baseLesson.id
  };
};

// Export individual course modules for direct access
export {
  CDL_MASTERY_ENHANCED,
  DISPATCHER_ENHANCED,
  TRUCKING_BUSINESS_ENHANCED,
  FREIGHT_BROKER_ENHANCED
};

// Default export with all utilities
export default {
  ENHANCED_COURSE_MAP,
  getEnhancedLesson,
  hasEnhancedContent,
  getEnhancedModules,
  getMergedLessonContent,
  CDL_MASTERY_ENHANCED,
  DISPATCHER_ENHANCED,
  TRUCKING_BUSINESS_ENHANCED,
  FREIGHT_BROKER_ENHANCED
};
